<template>
  <div class="evaluation-view">
    <div class="evaluation-view__navbar">
      <button
        class="button-icon evaluation-view__homebutton"
        type="button"
        :class="{
          '-is-active': currentTabItem == null
        }"
        :title="$t('labels.evaluationHome')"
        @click="openTab(null)"
      >
        <lucide-icon name="home" />
      </button>

      <button class="button-icon" type="button" :title="$t('labels.savedCharts')" @click="openLoadingMenu">
        <lucide-icon name="folder" />
      </button>

      <evaluation-loading-menu
        :visible="loadingMenu.visible"
        :x="loadingMenu.x"
        :y="loadingMenu.y"
        :open-evaluation-ids="new Set([...openEvaluationIds, 0])"
        :open-overview-ids="new Set(openOverviewIds)"
        :overviews-visible="true"
        @close="loadingMenu.visible = false"
        @open-evaluation="openTab({ type: 'evaluation', id: $event })"
        @open-overview="openTab({ type: 'evaluationOverview', id: $event })"
      />

      <!-- active simulation tab is fixed -->
      <evaluation-tab
        :evaluation-id="0"
        :index="-1"
        @select="openTab({ type: 'evaluation', id: 0 })"
        @download="downloadChartImage({ id: 0, type: 'evaluation' })"
        @create="openTab($event)"
      />

      <sortable-list
        :items="tabItemsByUid"
        :order="tabOrder"
        item-key="uid"
        direction="horizontal"
        class="evaluation-view__tablist"
        @update:order="updateTabOrder($event)"
      >
        <template #default="{ item, index }">
          <evaluation-tab
            v-if="item.type == 'evaluation'"
            :evaluation-id="item.id"
            :index="index"
            @select="openTab(item)"
            @download="downloadChartImage(item)"
            @close="closeTab(item)"
            @create="openTab($event)"
          />

          <evaluation-overview-tab
            v-else
            :overview-id="item.id"
            @select="openTab(item)"
            @close="closeTab(item)"
            @create="openTab($event)"
            @open-layouts="openOverviewLayouts(item, $event)"
          />
        </template>
      </sortable-list>

      <button
        class="button-icon"
        type="button"
        @click="createEvaluation()"
        @contextmenu.prevent="openNewContextmenu($event)"
      >
        <lucide-icon name="plus" />
      </button>

      <context-menu
        v-if="newContextmenu.visible"
        :x="newContextmenu.x"
        :y="newContextmenu.y"
        @close="newContextmenu.visible = false"
      >
        <button
          type="button"
          class="context-menu__entry"
          @click="createEvaluation()"
        >
          {{ $t("labels.newEvaluation") }}
        </button>
        <button
          type="button"
          class="context-menu__entry"
          @click="createEvaluationOverview()"
        >
          {{ $t("labels.newEvaluationOverview") }}
        </button>
      </context-menu>
    </div>

    <evaluation-chart
      v-if="currentTabItem?.type == 'evaluation'"
      ref="evaluationChartComponent"
      :key="currentTabItem.uid"
      :evaluation-id="currentTabItem.id"
      :data-editable="true"
      @close="closeTab(currentTabItem)"
    />
    <evaluation-overview
      v-else-if="currentTabItem?.type == 'evaluationOverview'"
      ref="overviewComponent"
      :evaluation-overview-id="currentTabItem.id"
      @open-evaluation="openTab({ type: 'evaluation', id: $event })"
    />
    <div v-else class="evaluation-view__mainpage">
      <evaluation-view-sidebar></evaluation-view-sidebar>

      <div class="evaluation-view__mainpage-content">
        <evaluation-chart-suggestions
          class="pt-0"
          :evaluation-id="null"
          @choose="openTab({ type: 'evaluation', id: $event.id })"
        >
          <template #grid>
            <button
              type="button"
              class="evaluation-view__button-new"
              @click="createEvaluation()"
            >
              {{ $t("actions.createEvaluation") }}
            </button>
          </template>
        </evaluation-chart-suggestions>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, nextTick, computed } from "vue";
import { getElementOrAncestorWithClass } from "@/helpers/utility/DOM";

import LucideIcon from "@core/ui/LucideIcon.vue";
import ContextMenu from "@core/ui/ContextMenu.vue";
import SortableList from "@core/ui/SortableList.vue";

import EvaluationTab from "@editor/items/evaluation/EvaluationTab.vue";
import EvaluationChart from "@editor/items/evaluation/EvaluationChart.vue";
import EvaluationOverview from "@editor/items/evaluation/EvaluationOverview.vue";
import EvaluationChartSuggestions from "@editor/items/evaluation/EvaluationChartSuggestions.vue";
import EvaluationViewSidebar from "./EvaluationViewSidebar.vue";
import EvaluationOverviewTab from "@editor/items/evaluation/EvaluationOverviewTab.vue";
import EvaluationLoadingMenu from "@editor/items/evaluation/EvaluationLoadingMenu.vue";

import { useEvaluationViewStore } from "./evaluationView.store";
import { useEvaluationStore } from "@editor/items/evaluation/evaluations.store";
import { useEvaluationOverviewStore } from "@editor/items/evaluation/evaluationOverviews.store";

import type { EvaluationItem } from "@editor/items/evaluation/EvaluationItem";
import type { EvaluationOverviewItem } from "@editor/items/evaluation/evaluationOverviews.store";
import type { Subset } from "@/helpers/utility/Type";

const evaluationChartComponent = ref<InstanceType<typeof EvaluationChart>>();
const overviewComponent = ref<InstanceType<typeof EvaluationOverview>>();

const evaluationStore = useEvaluationStore();
const evaluationViewStore = useEvaluationViewStore();

const tabOrder = computed(() => evaluationViewStore.tabOrder);
const currentTabItem = computed(() => evaluationViewStore.currentTabItem);
const openOverviewIds = computed(() => evaluationViewStore.openOverviewIds);
const openEvaluationIds = computed(() => evaluationViewStore.openEvaluationIds);
const tabItemsByUid = computed(() => evaluationViewStore.tabItemsByUid);

const { openTab, closeTab, updateTabOrder } = evaluationViewStore;

// ================================
// CREATION

async function createEvaluation(evaluationData: Subset<EvaluationItem> = {}) {
  const evaluation = await evaluationStore.create();

  if (evaluationData.chartType) {
    evaluationStore.setChartType(evaluation.id, evaluationData.chartType);
  }

  openTab(evaluation);
}

async function createEvaluationOverview() {
  const overview = await useEvaluationOverviewStore().create();
  openTab(overview);
}

// ================================
// LOADING MENU
// ------------------------------
// This is the contextmenu for the "load" icon button in the navbar.
// It provides the options to open existing evaluations or evaluation
// overviews.

const loadingMenu = reactive({
  visible: false,
  x: 0,
  y: 0
});

function openLoadingMenu(event: MouseEvent) {
  loadingMenu.visible = true;
  const target = event.target as HTMLElement;
  const button = getElementOrAncestorWithClass(target, "button-icon");
  if (button) {
    const rect = button.getBoundingClientRect();
    loadingMenu.x = rect.left + 10;
    loadingMenu.y = rect.bottom;
  } else {
    loadingMenu.x = event.clientX;
    loadingMenu.y = event.clientY;
  }
}

// ================================
// "NEW" CONTEXTMENU
// ------------------------------
// This is the contextmenu for the "new" button in the navbar.
// It provides the options to create a new evaluation OR a
// new evaluation overview.

const newContextmenu = reactive({
  visible: false,
  x: 0,
  y: 0
});

function openNewContextmenu(event: MouseEvent) {
  const button = (event.target as HTMLElement)?.closest("button");
  if (button) {
    const rect = button.getBoundingClientRect();
    newContextmenu.x = rect.left + 10;
    newContextmenu.y = rect.bottom;
  } else {
    newContextmenu.x = event.clientX;
    newContextmenu.y = event.clientY;
  }

  newContextmenu.visible = true;
}

// ================================
// TAB INTERACTIONS

function downloadChartImage(evaluation: { type: "evaluation"; id: number }) {
  openTab(evaluation);
  nextTick(() => {
    evaluationChartComponent.value?.openImageExportPopup();
  });
}

function openOverviewLayouts(
  overview: EvaluationOverviewItem,
  event?: MouseEvent
) {
  openTab(overview);
  nextTick(() => {
    overviewComponent.value?.openLayouts(event);
  });
}
</script>

<style lang="scss">
.evaluation-view {
  height: 100%;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content minmax(3rem, 1fr);

  &__navbar {
    position: relative;
    z-index: 2;
    overflow: hidden;

    display: flex;
    gap: 0.5rem;
    padding-top: 0.5rem;
    padding-inline: 1rem;
    min-height: 45px;

    background-color: var(--color-background);
    box-shadow: 0 1px 1px var(--color-shadow-1);
  }

  &__homebutton {
    &.-is-active {
      color: var(--color-text);
    }
  }

  &__button-new {
    background-color: var(--color-action);
    color: var(--color-action-contrast);
    font-size: 1rem;
    border-radius: 5px;
    border: none;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

    &:hover {
      box-shadow: 0 2px 8px var(--color-shadow-3);
    }
  }

  &__overviewtab {
    box-shadow: 1px 2px 4px var(--color-shadow-1);
    transition:
      background-color 250ms,
      box-shadow 250ms;
    background-color: var(--color-background);
    display: flex;
    white-space: nowrap;
    height: 100%;

    &:hover {
      background-color: var(--color-background-close);
    }
    &.-is-active {
      box-shadow: 0px 1px 4px var(--color-shadow-3);
      background-color: var(--color-background-close);
    }
  }
  &__overviewbutton {
    background-color: transparent;
    border: none;
    padding: 0.5rem 1rem;
    padding-right: 0.5rem;
  }

  &__tablist {
    flex-direction: row;
    max-width: 100%;
    min-width: 0;

    .sortable-list__entry {
      min-width: 0;
    }
  }

  &__mainpage {
    display: grid;
    grid-template-columns: var(--sidebar-width, 22rem) minmax(0, 1fr);
  }
  &__mainpage-content {
    padding: 1rem;
    overflow: auto;
  }
}
</style>
